import client from './client';
import { API_BASE } from './config';
import { getAccessToken } from '../storage/tokens';

// Axios wrappers for the server's /api/chatbot/* routes (chat_bot.md §2.3).
// The UI never talks to an AI vendor SDK directly — only through these.
export const getChatConfig = () => client.get('/chatbot/config');

export const createConversation = (title) => client.post('/chatbot/conversations', title ? { title } : {});

export const listConversations = (params) => client.get('/chatbot/conversations', { params });

export const getConversation = (id) => client.get(`/chatbot/conversations/${id}`);

export const sendMessage = (id, content) => client.post(`/chatbot/conversations/${id}/messages`, { content });

export const deleteConversation = (id) => client.delete(`/chatbot/conversations/${id}`);

// ends a conversation without deleting it — kept for the record (staff
// review, token history), the customer just can't continue it (see
// ChatContext's closeConversation, chatbotFunctions.js's closeConversation).
export const closeConversation = (id) => client.post(`/chatbot/conversations/${id}/close`);

// Streams the assistant's reply via SSE (chat_bot.md §2.4, Phase 4's
// `?stream=true` mode on the same send-message route). Native EventSource
// only supports GET requests with no custom headers, and this route needs
// POST + a bearer token, so this is a raw fetch + ReadableStream reader
// instead — there's no `client.js` axios instance involved here since axios
// doesn't expose a streaming response body in the browser.
//
// Dispatches parsed events to the matching handler as they arrive:
//   onDelta({ text }), onToolCallStart({ id, name }),
//   onToolCallEnd({ id, name, result }),
//   onMessage({ conversationId, userMessage, assistantMessage }),
//   onError(payload)
export const streamMessage = async (
  id,
  content,
  { onDelta, onToolCallStart, onToolCallEnd, onMessage, onError } = {}
) => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/chatbot/conversations/${id}/messages?stream=true`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ content }),
  });

  if (!response.ok || !response.body) {
    const err = new Error(`chat stream failed (${response.status})`);
    err.response = { status: response.status };
    throw err;
  }

  const dispatch = (event, data) => {
    if (event === 'delta') onDelta?.(data);
    else if (event === 'tool_call_start') onToolCallStart?.(data);
    else if (event === 'tool_call_end') onToolCallEnd?.(data);
    else if (event === 'message') onMessage?.(data);
    else if (event === 'error') onError?.(data);
  };

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIndex;
    while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);

      let event = 'message';
      let dataLine = '';
      for (const line of rawEvent.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
      }
      if (!dataLine) continue;

      try {
        dispatch(event, JSON.parse(dataLine));
      } catch {
        dispatch(event, dataLine);
      }
    }
  }
};
